/**
 * Um perfil candidato e as leituras dele (E151.F2, ADR-0103 §4): as que podem decidir o caso, na
 * ordem do servidor, com a tese, o fundamento e o que cada uma exige por expansão; e as regras do
 * perfil que ainda não decidem, com o motivo. Nada aqui reordena ou escolhe: é a seleção do servidor.
 */
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  dataBr,
  exigiveis,
  leiturasQueDecidem,
  leiturasQueNaoDecidem,
  naoExigidosNaData,
  provaAdicional,
  type LeituraSelecionadaData,
  type ModoDoExame,
  type PerfilCandidatoData,
  type RequisitoSelecionadoData,
} from '@/lib/formalizacao';

const t = ptBR.formalizacao;

function Requisito({ requisito }: { requisito: RequisitoSelecionadoData }): JSX.Element {
  const marcas = [
    `${t.categoria[requisito.categoria]}, ${t.fonte[requisito.fonte_da_forma]}`,
    { ativa: null, verificada: t.naoAtivado, preparada: t.naoConferido }[requisito.estado],
    requisito.situacao_da_incidencia === 'nao_apurada' ? t.incidenciaNaoApurada : null,
  ].filter((m): m is string => m !== null);
  return (
    <li data-testid="formalizacao-requisito" data-requisito={requisito.id}>
      <span className="text-fg">{requisito.titulo}</span>{' '}
      <span className="text-fg-muted">({marcas.join(' · ')})</span>
    </li>
  );
}

function Leitura({
  leitura,
  primeira,
}: {
  leitura: LeituraSelecionadaData;
  primeira: boolean;
}): JSX.Element {
  const adicionais = provaAdicional(leitura);
  const naoExigidos = naoExigidosNaData(leitura);
  return (
    <li
      className="border border-border p-sc-3"
      data-testid="formalizacao-leitura"
      data-leitura={leitura.id}
    >
      <details>
        <summary className="cursor-pointer text-body-md text-fg">
          {leitura.titulo} {primeira ? <Badge variant="info">{t.primeira}</Badge> : null}
        </summary>
        <dl className="mt-sc-2 space-y-sc-2 text-body-sm">
          <div>
            <dt className="text-fg-muted">{t.tese}</dt>
            <dd className="text-fg">{leitura.tese}</dd>
          </div>
          <div>
            <dt className="text-fg-muted">{t.fundamento}</dt>
            <dd className="text-fg">{leitura.fundamento}</dd>
          </div>
        </dl>
        <p className="mt-sc-2 text-body-sm text-fg-muted">{t.exigencias}</p>
        <ul className="list-disc pl-sc-6 text-body-sm">
          {exigiveis(leitura).map((r) => (
            <Requisito key={r.id} requisito={r} />
          ))}
        </ul>
        {adicionais.length > 0 ? (
          <>
            <p className="mt-sc-2 text-body-sm text-fg-muted">{t.provaAdicional}</p>
            <ul className="list-disc pl-sc-6 text-body-sm">
              {adicionais.map((r) => (
                <Requisito key={r.id} requisito={r} />
              ))}
            </ul>
          </>
        ) : null}
        {naoExigidos.length > 0 ? (
          <>
            <p className="mt-sc-2 text-body-sm text-fg-muted">{t.naoExigidosNaData}</p>
            <ul className="list-disc pl-sc-6 text-body-sm" data-testid="formalizacao-nao-exigidos">
              {naoExigidos.map((r) => (
                <li key={r.id} data-requisito={r.id}>
                  <span className="text-fg">{r.titulo}</span>{' '}
                  <span className="text-fg-muted">— {r.motivo}</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </details>
    </li>
  );
}

export function LeiturasDoPerfil({
  candidato,
  modo,
}: {
  candidato: PerfilCandidatoData;
  modo: ModoDoExame;
}): JSX.Element {
  const decidem = leiturasQueDecidem(candidato);
  const naoDecidem = leiturasQueNaoDecidem(candidato);
  const marcoNaoConferido = !candidato.inicio_conferido || !candidato.fim_conferido;
  return (
    <section
      className="space-y-sc-3"
      data-testid="formalizacao-perfil"
      data-perfil={candidato.perfil_id}
      aria-label={`${candidato.titulo}, ${dataBr(candidato.data)}`}
    >
      <div>
        <p className="text-body-md font-semibold text-fg">
          {candidato.titulo} · {dataBr(candidato.data)}
        </p>
        {candidato.por_leitura_normativa ? (
          <p className="text-body-sm text-fg-muted">{t.porLeituraNormativa}</p>
        ) : null}
        {marcoNaoConferido ? (
          <p className="text-body-sm text-fg-muted" data-testid="formalizacao-marco">
            {t.marcoNaoConferido}
          </p>
        ) : null}
      </div>
      <p className="text-body-sm font-semibold text-fg">{t.leiturasQueDecidem[modo]}</p>
      {decidem.length > 0 ? (
        <ol className="space-y-sc-2">
          {decidem.map((l, i) => (
            <Leitura key={l.id} leitura={l} primeira={modo === 'parte' && i === 0} />
          ))}
        </ol>
      ) : (
        <p className="text-body-sm text-danger-700">{t.nenhumaLeitura}</p>
      )}
      {naoDecidem.length > 0 ? (
        <details data-testid="formalizacao-nao-decidem">
          <summary className="cursor-pointer text-body-sm text-fg-muted">
            {t.regrasQueNaoDecidem.replace('{n}', String(naoDecidem.length))}
          </summary>
          <ul className="mt-sc-2 list-disc pl-sc-6 text-body-sm">
            {naoDecidem.map((l) => (
              <li key={l.id} data-leitura={l.id}>
                <span className="text-fg">{l.titulo}</span>{' '}
                <span className="text-fg-muted">
                  ({l.motivos.map((m) => t.motivo[m]).join(' · ')})
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
